import { useMemo, useState } from 'react'
import { LabFlow } from './LabFlow'
import {
  DemoHeader,
  InsightCard,
  OntologyResponsePanel,
  UserActionPanel,
} from './GoalDrivenDemo'
import { BASELINE_CONDITIONS, getPathGenerators } from '../../data/layers'
import { useT } from '../../lib/i18n'
import type { OntoPathNode } from '../../types'
import type { LabDemoProps } from './types'

function computeChain(path: OntoPathNode[], rootId: string): OntoPathNode[] {
  // BFS upward from rootId; returns nodes in causal order (root first, ancestors after).
  const byId = new Map(path.map((n) => [n.id, n]))
  const out: OntoPathNode[] = []
  const seen = new Set<string>()
  const queue = [rootId]
  while (queue.length > 0) {
    const id = queue.shift()!
    if (seen.has(id)) continue
    seen.add(id)
    const node = byId.get(id)
    if (!node) continue
    out.push(node)
    node.parents?.forEach((pid) => queue.push(pid))
  }
  return out
}

export function ExplainabilityDemo({ paradigm, lab, data }: LabDemoProps) {
  const t = useT()
  const demo = (data as typeof t.labDemos.explainability | undefined) ?? t.labDemos.explainability
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const selected = selectedId ? demo.decisions.find((d) => d.id === selectedId) ?? null : null

  const generators = useMemo(() => getPathGenerators(t), [t])
  const path = useMemo(
    () => (selected ? generators[selected.goalId](BASELINE_CONDITIONS) : []),
    [generators, selected],
  )

  const chain = useMemo(
    () => (selected ? computeChain(path, selected.nodeId) : []),
    [path, selected],
  )

  return (
    <div className="space-y-5">
      <DemoHeader paradigm={paradigm} />

      <div className="flex flex-col gap-4">
        <UserActionPanel
          label={lab.userActionLabel}
          title={demo.decisionsLabel}
          hint={demo.decisionsHint}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {demo.decisions.map((d) => {
              const active = d.id === selectedId
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setSelectedId((curr) => (curr === d.id ? null : d.id))}
                  aria-pressed={active}
                  className={`text-left px-3 py-2.5 rounded-[var(--radius-sm)] transition-colors leading-snug ${
                    active
                      ? 'bg-[color:var(--color-orion-amber)]/15 border-l-2 border-[color:var(--color-orion-amber)]'
                      : 'border-l-2 border-transparent border-faint hover:bg-[color:var(--color-orion-deep)]/40'
                  }`}
                >
                  <span
                    className={`text-[length:var(--text-small)] ${
                      active ? 'text-[color:var(--color-orion-text)]' : 'text-[color:var(--color-orion-haze)]'
                    }`}
                  >
                    {d.question}
                  </span>
                </button>
              )
            })}
          </div>
          {selected && (
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="mt-3 mono text-[length:var(--text-micro)] uppercase tracking-widest px-2 py-1 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-night)] transition-colors"
            >
              {demo.clearButton}
            </button>
          )}
        </UserActionPanel>

        <OntologyResponsePanel label={lab.ontologyResponseLabel}>
          {selected ? (
            <div className="space-y-4">
              <LabFlow
                path={path}
                kindLabels={t.l3.kindLabels}
                attributionRootId={selected.nodeId}
                pathKey={`explainability::${selected.id}`}
                minHeight={340}
                maxHeight={480}
              />
              <CausalChain
                chain={chain}
                kindLabels={t.l3.kindLabels}
                title={demo.chainTitle}
                hint={demo.chainHint}
              />
            </div>
          ) : (
            <EmptyState title={demo.emptyStateTitle} body={demo.emptyStateBody} />
          )}
        </OntologyResponsePanel>
      </div>

      <InsightCard title={demo.insightTitle} body={demo.insightBody} />
    </div>
  )
}

function CausalChain({
  chain,
  kindLabels,
  title,
  hint,
}: {
  chain: OntoPathNode[]
  kindLabels: Record<string, string>
  title: string
  hint: string
}) {
  return (
    <div className="border border-[color:var(--color-orion-surface)]/40 rounded-[var(--radius-md)] p-4">
      <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
        <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
          {title}
        </span>
        <span className="text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">{hint}</span>
      </div>
      <ol className="space-y-1">
        {chain.map((node, i) => (
          <li
            key={node.id}
            className="flex items-start gap-3 text-[length:var(--text-small)] leading-snug"
            style={{ paddingLeft: `${Math.min(i, 6) * 12}px` }}
          >
            <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--color-orion-haze)] pt-0.5 min-w-[64px]">
              {kindLabels[node.kind] ?? node.kind}
            </span>
            <span className="text-[color:var(--color-orion-text)]">{node.label}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-[260px] flex flex-col items-center justify-center text-center px-6">
      <div className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
        {title}
      </div>
      <p
        className="text-[color:var(--color-orion-haze)] max-w-md"
        style={{ fontSize: 'var(--text-small)', lineHeight: 1.6 }}
      >
        {body}
      </p>
    </div>
  )
}
